import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import PatientForm from "../patientForm/patientForm";
import HealthInformation from "../healthInformation/healthInformation";
import ImageUpload from "../imageUpload/imageUpload";
import PatientInference from "../patientInference/patientInference";
import ProcessingScreen from "../processingScreen/processingScreen";
import PatientScreening from "../patientScreening/patientScreening";
import ExitForm from "../patientScreening/exitForm";
import axios from "axios";
import config from "../../config";
import { RequiredRefferal, handleSetReferral, nextSteps, handleSetDR, convertDRGradeToDisplay } from "../../utlis";
import { v4 as uuidv4 } from 'uuid';
import { generateSecureUUID } from '../../utils/uuidUtils';
import { useModel } from '../../context/ModelContext';
import { db, saveImageHash, markImageHashesAsSynced, getCurrentUserId, getScreeningImageDraft, saveScreeningImageDraft, clearScreeningImageDraft, getPersistedModelDownloadVersion } from "../../db";
import { generateImageHash } from "../../utils/imageHashing";
import { Snackbar, Alert } from "@mui/material";
import SyncValidationGuard from "../syncValidationGuard/SyncValidationGuard";
import FullPageLoader from "../helper/FullPageLoader";
import { isOffline as checkIsOffline } from "../../utils/offlineDetection";
import { isMobileBrowser } from "../../utils/deviceDetection";
import { captureAppError } from "../../utils/captureAppError";
import { initOpenCVWorker } from "../../services/opencvWorkerManager";
import { submitReportWithPresign, multipartFallback, PresignNotSupportedError, ResumeUploadBlockedError, applyDiabetesDurationForReportApi } from "../../services/reportUploadService";
import { normalizeModelTrainingConsent } from "../../utils/modelTrainingConsent";
const pat_info = {
    patientType: "new_patient",
    name: "",
    gender: "",
    age: "",
    otherId: "",
    abhaId: "",
    number: "",
    diabetes:"",
    diabetesDurationYears: "",
    diabetesDurationMonths: "",
    visualAcuity: {
        distant: {
            left: "",
            right: "",
        },
        near: {
            left: "",
            right: "",
        },
    },
};

const health_info = {
    preDiagnosedDiabetes: "",
    durationDiabetesDetection: "",
    diabetesType: "",
    HbA1CLevel: "",
    systolicBloodPressure: "",
    diastolicBloodPressure: "",
    otherComplications: [],
    visualAcuity: {
        left: "",
        right: "",
    },
    familyHistory: [],
    notes: ""
};

const image_file = {
    left: {
        gradabale: "",
        image: {},
        reason: "",
        classification: {},
        gradability: {},
        ishazy: false,
        operator_override_fundus: false,
        operator_override_eye: false,
        model_outputs: {
            fundus: null,
            eyes: null,
            drdme: null
        }
    },
    right: {
        gradabale: "",
        image: {},
        reason: "",
        classification: {},
        gradability: {},
        ishazy: false,
        operator_override_fundus: false,
        operator_override_eye: false,
        model_outputs: {
            fundus: null,
            eyes: null,
            drdme: null
        }
    },
    model_training_consent: false,
    camera_used:"",
    single_image_reason: ""
};

const validationSchema = Yup.object().shape({
    pat_info: Yup.object().shape({
        gender: Yup.string().required('Gender is required'),
        age: Yup.number()
            .min(0, 'Age cannot be negative')
            .max(100, 'Max age is 100')
            .required('Age is required'),
        otherId: Yup.string()
            .matches(/^[A-Za-z0-9]+$/, 'ID Value should be alphanumeric')
            .required(''),
        diabetes: Yup.string()
            .oneOf(["Yes", "No", "Unknown"], 'Invalid value for Diabetes')
            .required('Diabetes status is required'),
        diabetesDurationYears: Yup.number()
            .nullable()
            .transform((value, originalValue) => {
                // Convert empty string to null
                return originalValue === '' ? null : value;
            })
            .test('validate if filled', function(value) {
                const diabetesValue = this.parent.diabetes;
                // Only validate if Diabetes is Yes AND a value is provided
                if (diabetesValue === 'Yes' && value !== null && value !== undefined && value !== '') {
                    if (value < 0) {
                        return this.createError({ message: 'Years cannot be negative' });
                    }
                    if (value > 100) {
                        return this.createError({ message: 'Years cannot exceed 100' });
                    }
                }
                return true; // Always valid if empty or Diabetes is not Yes
            }),
        diabetesDurationMonths: Yup.number()
            .nullable()
            .transform((value, originalValue) => {
                // Convert empty string to null
                return originalValue === '' ? null : value;
            })
            .test('validate if filled', function(value) {
                const diabetesValue = this.parent.diabetes;
                // Only validate if Diabetes is Yes AND a value is provided
                if (diabetesValue === 'Yes' && value !== null && value !== undefined && value !== '') {
                    if (value < 0) {
                        return this.createError({ message: 'Months cannot be negative' });
                    }
                    if (value > 11) {
                        return this.createError({ message: 'Months cannot exceed 11' });
                    }
                }
                return true; // Always valid if empty or Diabetes is not Yes
            }),
        visualAcuity: Yup.object().shape({
            distant: Yup.object().shape({
                left: Yup.string()
                    .nullable()
                    .test('validate if filled', 'Invalid value. Please select from the dropdown options.', function(value) {
                        if (!value || (typeof value === 'string' && value.trim() === '')) {
                            return true; // Optional field
                        }
                        const trimmedValue = String(value).trim();
                        const validOptions = ["6/6", "6/9", "6/12", "6/18", "6/24", "6/36", "6/60", "5/60", "4/60", "3/60", "2/60", "1/60", "FCCF", "CF", "HMCF", "HM", "PL+", "PL-", "NPL"];
                        return validOptions.includes(trimmedValue);
                    }),
                right: Yup.string()
                    .nullable()
                    .test('validate if filled', 'Invalid value. Please select from the dropdown options.', function(value) {
                        if (!value || (typeof value === 'string' && value.trim() === '')) {
                            return true; // Optional field
                        }
                        const trimmedValue = String(value).trim();
                        const validOptions = ["6/6", "6/9", "6/12", "6/18", "6/24", "6/36", "6/60", "5/60", "4/60", "3/60", "2/60", "1/60", "FCCF", "CF", "HMCF", "HM", "PL+", "PL-", "NPL"];
                        return validOptions.includes(trimmedValue);
                    }),
            }),
            near: Yup.object().shape({
                left: Yup.string()
                    .nullable()
                    .test('validate if filled', 'Invalid value. Please select from the dropdown options.', function(value) {
                        if (!value || (typeof value === 'string' && value.trim() === '')) {
                            return true; // Optional field
                        }
                        const trimmedValue = String(value).trim();
                        const validOptions = ["N5", "N6", "N8", "N10", "N12", "N14", "N18", "N24", "N36"];
                        return validOptions.includes(trimmedValue);
                    }),
                right: Yup.string()
                    .nullable()
                    .test('validate if filled', 'Invalid value. Please select from the dropdown options.', function(value) {
                        if (!value || (typeof value === 'string' && value.trim() === '')) {
                            return true; // Optional field
                        }
                        const trimmedValue = String(value).trim();
                        const validOptions = ["N5", "N6", "N8", "N10", "N12", "N14", "N18", "N24", "N36"];
                        return validOptions.includes(trimmedValue);
                    }),
            }),
        }),
    }),
    health_info: Yup.object().shape({
        preDiagnosedDiabetes: Yup.string().required("Pre-diagnosed Diabetes is required"),
        durationDiabetesDetection: Yup.number().test('Conditional Required', "Required", function(value) {
            const firstFieldValue = this.parent.preDiagnosedDiabetes;
            return firstFieldValue === 'yes' ? !!value : true;
            })
            .min(0, 'Year Cannot be less than 0'),
        diabetesType: Yup.string().test('Conditional Required', "Required", function(value) {
            const firstFieldValue = this.parent.preDiagnosedDiabetes;
            return firstFieldValue === 'yes' ? !!value : true;
        }),
        HbA1CLevel: Yup.number()
            .min(0, 'Cannot be negative'),
        systolicBloodPressurebloodPressure: Yup.number()
            .min(0, "Cannot be negative"),
        diastolicBloodPressure: Yup.number()
            .min(0, "Cannot be negative"),
        visualAcuity: Yup.object().shape({
            left: Yup.string().required("Both left and right are required!!!")
            .matches(/^(?:[0-9]\/[0-9]{1,2}|PL-|PL\+|HMCF|FCCF)?$/, 'Visual Acuity should be in the format "X/YY" or PL- or PL+ or HMCF or FCCF'),
            right: Yup.string().required("Both left and right are required!!!")
            .matches(/^(?:[0-9]\/[0-9]{1,2}|PL-|PL\+|HMCF|FCCF)?$/, 'Visual Acuity should be in the format "X/YY" or PL- or PL+ or HMCF or FCCF'),
        }),
    }),
});

const PatientScreeningWrapper = ({userData}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const header = {"Authorization": "Token "+localStorage.getItem("optha-token")};
    const { session, isLoading: modelLoading, error: modelError, loadModel } = useModel();
    
    useEffect(() => {
        // Model should already be loaded from splash screen
        // Only try to load model if session is truly missing AND not currently loading
        // Add a longer delay to ensure splash screen has finished.
        // On iOS Safari we load/release sessions one at a time during inference — session being
        // null after a run is intentional, so skip the watchdog entirely on that platform.
        if (isMobileBrowser()) return; // Phones load/release sessions per image; watchdog would re-load needlessly

        if (!session && !modelLoading) {
            // Wait longer to ensure splash screen has completed loading
            const timer = setTimeout(() => {
                if (!session && !modelLoading) {
                    loadModel().catch(err => {
                        captureAppError(err, {
                            tags: {
                                errorType: 'model_loading_error',
                                errorStage: 'patient_screening',
                            },
                        });
                    });
                }
            }, 2000);
            
            return () => clearTimeout(timer);
        }
    }, [session, modelLoading, loadModel]); // React to session and loading state changes

    // Check for edit mode from URL parameters
    const urlParams = new URLSearchParams(location.search);
    const isEditMode = urlParams.get('edit') === 'true';
    const editStep = urlParams.get('step');

    // Check if report_id exists and redirect to report page on refresh
    useEffect(() => {
        const reportId = localStorage.getItem("report_id");
        const patientId = localStorage.getItem("patient_id");
        const draftStr = localStorage.getItem('patient_form_draft');
        
        // If we're on /patientscreen and not in edit mode
        if (location.pathname === '/patientscreen' && !isEditMode) {
            // If there's a report_id, try to redirect to the report page
            // This handles both cases: when patient_id exists and when it doesn't (after successful online submission)
            if (reportId) {
                // Check if offline using API health check
                checkIsOffline().then((isOffline) => {
                    if (isOffline) {
                        // When offline, stay on current screen (don't redirect to sync)
                        setCheckingRedirect(false);
                    } else {
                        // When online, navigate to the report page
                        navigate(`/patientrecord/${reportId}`, { replace: true });
                    }
                });
            }
            // No report_id but draft exists → check if classification data exists (report was submitted)
            else if (!reportId && draftStr) {
                try {
                    const draft = JSON.parse(draftStr);
                    const formData = draft?.formData;
                    
                    // Check if left or right classification exists (not empty object)
                    const leftClassification = formData?.image_file?.left?.classification;
                    const rightClassification = formData?.image_file?.right?.classification;
                    
                    const hasClassification = 
                        (leftClassification && Object.keys(leftClassification).length > 0 && leftClassification.predicted_class_name) ||
                        (rightClassification && Object.keys(rightClassification).length > 0 && rightClassification.predicted_class_name);
                    
                    if (hasClassification) {
                        // Classification exists → report was submitted → find report in IndexDB and redirect to patient record
                        const findAndRedirectToReport = async () => {
                            try {
                                const userId = getCurrentUserId();
                                
                                if (!userId) {
                                    // Stay on current screen (e.g. Patient Inference) - don't redirect to sync
                                    setCheckingRedirect(false);
                                    return;
                                }
                                
                                // Get most recent pending report for current user
                                const pendingReports = await db.reports
                                    .where('optometrist_id').equals(userId)
                                    .and(report => report.syncStatus === 'pending')
                                    .toArray();
                                
                                if (pendingReports.length > 0) {
                                    // Sort by createdAt, get most recent
                                    const mostRecent = pendingReports.sort((a, b) => 
                                        new Date(b.createdAt) - new Date(a.createdAt)
                                    )[0];
                                    
                                    // Redirect to patient record page (it can load from IndexDB)
                                    navigate(`/patientrecord/${mostRecent.id}`, { replace: true });
                                } else {
                                    // No pending reports (e.g. just submitted offline) → stay on current screen
                                    setCheckingRedirect(false);
                                }
                            } catch (error) {
                                setCheckingRedirect(false);
                            }
                        };
                        
                        findAndRedirectToReport();
                    } else {
                        setCheckingRedirect(false);
                    }
                    // No classification → user is starting fresh → stay on form (do nothing)
                } catch (error) {
                    setCheckingRedirect(false);
                }
            } else {
                setCheckingRedirect(false);
            }
        } else {
            // Not on /patientscreen or in edit mode → no redirect check needed
            setCheckingRedirect(false);
        }
    }, [location.pathname, isEditMode, navigate]);
    const patient = localStorage.getItem('patient_id');
    const idexists = !!patient;

    // Simple localStorage keys
    const STORAGE_KEY = 'patient_form_draft';
    
    // Load saved form data from localStorage on mount
    const loadSavedFormData = () => {
        if (isEditMode) {
            // Don't load saved data in edit mode
            return {
            pat_info: pat_info,
            health_info: health_info,
            image_file: image_file
            };
        }
        
        // ALWAYS check for saved data first, regardless of patient_id
        // (user might be on step 1 before patient is created)
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed.formData) {
                    // FIX: Deep-merge all nested objects to preserve all fields
                    const loadedData = {
                        pat_info: {
                            ...pat_info,  // Start with defaults
                            ...parsed.formData.pat_info  // Override with saved data
                        },
                        health_info: {
                            ...health_info,  // Start with defaults
                            ...parsed.formData.health_info  // Override with saved data
                        },
                        image_file: {
                            left: {
                                ...image_file.left,  // Start with defaults
                                ...parsed.formData.image_file?.left  // Override with saved data
                            },
                            right: {
                                ...image_file.right,  // Start with defaults
                                ...parsed.formData.image_file?.right  // Override with saved data
                            },
                            model_training_consent: normalizeModelTrainingConsent(
                                parsed.formData.image_file?.model_training_consent
                            ),
                            camera_used: parsed.formData.image_file?.camera_used || "",
                            single_image_reason: parsed.formData.image_file?.single_image_reason || ""
                        }
                    };
                    
                    return loadedData;
                }
            } catch (error) {
                /* malformed draft — fall through */
            }
        }
        
        // Only if no saved data AND no patient_id, this is a completely new patient
        if (!idexists && !saved) {
            localStorage.removeItem(STORAGE_KEY);
            return {
                pat_info: pat_info,
                health_info: health_info,
                image_file: image_file
            };
        }
        
        // If no saved data but patient_id exists, use default
        return {
            pat_info: pat_info,
            health_info: health_info,
            image_file: image_file
        };
    };
    
    // Load saved state from localStorage
    const loadSavedState = () => {
        if (isEditMode) {
            return {
                step: editStep ? parseInt(editStep) : 0,
                consent: true,
                mlConsentGiven: false
            };
        }
        
        // ALWAYS check for saved data first, regardless of patient_id
        // (user might be on step 1 before patient is created)
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                
                // Validate step is within range - use saved step if valid, otherwise default to 0
                // Don't use idexists to determine step - just having patient_id doesn't mean user progressed
                let savedStep = parsed.step !== undefined && parsed.step >= 0 && parsed.step <= 2 
                    ? parsed.step 
                    : 0; // Default to step 0 if step is invalid
                
                // REMOVED: Don't infer step from form data - trust the saved step value
                // A user can be on step 0 with form data filled in (e.g., just filled patient ID)
                // The saved step is the source of truth
                
                // Check if there's actual form data - if so, user must have given consent
                const hasFormData = parsed.formData && (
                    parsed.formData.pat_info?.name ||
                    parsed.formData.pat_info?.age ||
                    parsed.formData.pat_info?.gender ||
                    parsed.formData.pat_info?.otherId ||
                    parsed.formData.health_info?.preDiagnosedDiabetes
                );
                
                // Determine consent: if step > 0, consent must be true
                // If step is 0 but there's form data, user must have given consent to fill it
                // Otherwise, use saved consent value or fallback to idexists
                let determinedConsent;
                if (savedStep > 0) {
                    determinedConsent = true; // Can't be on step 1+ without consent
                } else if (hasFormData) {
                    determinedConsent = true; // If form data exists, user must have given consent
                } else {
                    determinedConsent = parsed.consent !== undefined ? parsed.consent : (idexists ? true : false);
                }
                
                // Use saved state - this is the source of truth
                const result = {
                    step: savedStep,
                    consent: determinedConsent,
                    mlConsentGiven: parsed.mlConsentGiven !== undefined ? parsed.mlConsentGiven : false
                };
                
                return result;
            }
        } catch (error) {
            console.error('[loadSavedState] Error parsing saved data:', error);
            // Error loading saved state
        }
        
        // Only if no saved data exists, default to step 0
        // Just having patient_id doesn't mean user has progressed past step 0
        // User might still be filling out the form on step 0
        // But if patient_id exists, user likely gave consent to get to patient detail page
        return {
            step: 0, // Always default to step 0 if no saved data
            consent: idexists ? true : false, // Use idexists only for consent, not step
            mlConsentGiven: false
        };
    };
    
    const savedState = loadSavedState();
    
    // Ensure step is valid (0, 1, or 2) and consent is consistent
    // Default to step 0 if invalid - don't use idexists to determine step
    const validatedStep = savedState.step >= 0 && savedState.step <= 2 ? savedState.step : 0;
    // If step > 0, consent MUST be true (user can't be on step 1+ without consent)
    const validatedConsent = validatedStep > 0 ? true : savedState.consent;
    
    const formik = useFormik({
        initialValues: loadSavedFormData(),
        validationSchema: validationSchema,
    });
    const [inferenceState, setInferenceState] = useState({
        inference: {
            "left": "",
            "right": "",
            "req": "",
        },
        inferenceProvided: false,
    });
    
    // Store classification results directly to avoid formik timing issues
    const [classificationResults, setClassificationResults] = useState({
        left: null,
        right: null
    });
    const [isLoading, setIsLoading] = useState(false);
    const [onExit, setOnExit] = useState(false);
    const [consent, setConsent] = useState(validatedConsent);
    const [mlConsentGiven, setMlConsentGiven] = useState(savedState.mlConsentGiven);
    const [step, setStep] = useState(validatedStep);
    /** Bumps when user starts a fresh screening from inference ("Add new patient") so ImageUpload remounts with clean non-fundus attempt state. */
    const [imageUploadSessionKey, setImageUploadSessionKey] = useState(0);
    const [submittingReport, setSubmittingReport] = useState(false);
    const [reportSubmitted, setReportSubmitted] = useState(false);
    const [autoSubmitAfterInference, setAutoSubmitAfterInference] = useState(false);
    const [showOfflineSnackbar, setShowOfflineSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // 'info', 'warning', 'error', 'success'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [checkingRedirect, setCheckingRedirect] = useState(true);

    // Track if this is the initial mount - MUST be at component level
    const isInitialMount = useRef(true);
    // After clearSavedData(), formik may still hold images briefly; debounced save would re-write IDB + localStorage unless we skip until a new flow (e.g. Add New Patient).
    const suppressPersistAfterClearRef = useRef(false);

    // FIX: Prevent backend from overwriting saved draft data
    // CRITICAL: Only check draft existence, never check inference state
    useEffect(() => {
        if (!idexists) return; // No patient → never call backend

        // --- Check if a local draft exists FIRST ----
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                
                const hasDraft =
                    parsed?.formData?.pat_info?.name ||
                    parsed?.formData?.pat_info?.age ||
                    parsed?.formData?.pat_info?.gender ||
                    parsed?.formData?.health_info?.preDiagnosedDiabetes;

                if (hasDraft) {
                    return; // Skip backend call if draft exists
                }
            } catch (err) {
                /* ignore malformed draft */
            }
        }

        // --- Only reach here if NO draft exists ----
            axios
            .get(`${config.API_BASE_URL}/api/auth/patients/?id=${patient}`, {headers: header})
            .then((res) => {
                const data = res?.data?.results?.[0];
                if (!data) return;

                if (data.name) formik.setFieldValue("pat_info.name", data.name);
                if (data.age) formik.setFieldValue("pat_info.age", data.age);
                if (data.sex) formik.setFieldValue("pat_info.gender", data.sex);

                if (data.abha_id !== null) {
                    formik.setFieldValue("pat_info.uniqueId.idType", "abha_id");
                    formik.setFieldValue("pat_info.uniqueId.idValue", data.abha_id);
                } else if (data.other_id !== null) {
                    formik.setFieldValue("pat_info.uniqueId.idType", "other_id");
                    formik.setFieldValue("pat_info.uniqueId.idValue", data.other_id);
                }

                if (data.phone) {
                    formik.setFieldValue("pat_info.number", data.phone);
                }
            })
            .catch(() => {});
    }, [idexists]); // <--- ONLY RUN ONCE when patient exists

    // Load existing data for edit mode
    useEffect(() => {
        if (isEditMode) {
            const editReportId = urlParams.get('reportId');
            if (editReportId) {
                loadReportDataForEdit(editReportId);
            }
        }
    }, [isEditMode]);

    // Drop stale IDB image rows when there is no in-progress form draft (e.g. after submit + dashboard "Add patient").
    useEffect(() => {
        if (isEditMode) return;
        if (!localStorage.getItem(STORAGE_KEY)) {
            clearScreeningImageDraft().catch(() => {});
        }
    }, [isEditMode]);

    // Reload recovery: images live in IndexedDB only while a patient_form_draft exists in localStorage.
    const imageDraftRestoredRef = useRef(false);
    useEffect(() => {
        if (isEditMode) return;
        if (step < 1) {
            imageDraftRestoredRef.current = false;
            return;
        }
        if (imageDraftRestoredRef.current) return;

        const savedFormDraft = localStorage.getItem(STORAGE_KEY);
        if (!savedFormDraft) {
            clearScreeningImageDraft().catch(() => {});
            return;
        }

        const patientId = localStorage.getItem('patient_id');
        const reportId = localStorage.getItem('report_id');

        getScreeningImageDraft()
            .then((draft) => {
                if (!draft || (!draft.left_image && !draft.right_image)) return;
                const idsMatch = draft.patient_id === patientId && draft.report_id === reportId;
                const draftHasNoIds = draft.patient_id == null && draft.report_id == null;
                if (!idsMatch && !draftHasNoIds) {
                    clearScreeningImageDraft().catch(() => {});
                    return;
                }

                imageDraftRestoredRef.current = true;
                const updates = { ...formik.values.image_file };
                if (draft.left_image && draft.left_image.data) {
                    updates.left = { ...updates.left, image: draft.left_image };
                }
                if (draft.right_image && draft.right_image.data) {
                    updates.right = { ...updates.right, image: draft.right_image };
                }
                formik.setFieldValue('image_file', updates);
            })
            .catch(() => {});
    }, [step, isEditMode]);

    // Keep a ref of latest formik values so image draft save (inside timeout) always reads current, not stale closure
    const formikValuesRef = useRef(formik.values);
    formikValuesRef.current = formik.values;

    // Save form data to localStorage whenever it changes (debounced)
    useEffect(() => {
        if (isEditMode) return; // Don't save in edit mode
        
        // CRITICAL: Skip save on initial mount - we just loaded from localStorage
        // This MUST be the first check to prevent overwriting saved step on refresh
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        
        const timeoutId = setTimeout(() => {
            try {
                if (suppressPersistAfterClearRef.current) return;
                const currentValues = formikValuesRef.current;
                // Validate step before saving
                const stepToSave = (step >= 0 && step <= 2) ? step : 0;
                
                // Check if form has actual data (not just empty strings)
                const hasActualData = currentValues.pat_info?.name ||
                                    currentValues.pat_info?.age ||
                                    currentValues.pat_info?.gender ||
                                    currentValues.pat_info?.otherId ||
                                    currentValues.health_info?.preDiagnosedDiabetes;
                
                // Determine consent to save:
                // - If step > 0, consent must be true
                // - If there's form data, user must have given consent to fill it, so save as true
                // - Otherwise, use current consent value
                const consentToSave = stepToSave > 0 ? true : (hasActualData ? true : consent);
                
                // Prepare form data to save (without images)
                const formDataToSave = {
                    ...currentValues,
                    image_file: {
                        left: {
                            ...currentValues.image_file.left,
                            image: {} // Don't save image file
                        },
                        right: {
                            ...currentValues.image_file.right,
                            image: {} // Don't save image file
                        },
                        model_training_consent: normalizeModelTrainingConsent(
                            currentValues.image_file.model_training_consent
                        ),
                        camera_used: currentValues.image_file.camera_used,
                        single_image_reason: currentValues.image_file.single_image_reason
                    }
                };
                
                // Prepare the complete data structure to save
                const dataToSave = {
                    formData: formDataToSave,
                    step: stepToSave,
                    consent: consentToSave,
                    mlConsentGiven
                };
                
                // Save if we have actual form data OR if step > 0 OR if consent is true
                // This ensures consent state is saved even when no form data is filled yet
                if (!hasActualData && stepToSave === 0 && !consentToSave) {
                    return;
                }

                // Real images live in IndexedDB only; formDataToSave always uses image: {} for localStorage.
                // Dedup compares that JSON — adding a File to formik often does NOT change the string, so we must
                // never skip screening_image_draft just because localStorage snapshot is unchanged.
                const leftImg = currentValues.image_file?.left?.image;
                const rightImg = currentValues.image_file?.right?.image;
                const isSaveable = (v) => v && (
                    v instanceof File ||
                    v instanceof Blob ||
                    (typeof v === 'object' && v.data && v.type) ||
                    (Array.isArray(v) && v.length > 0 && v[0]?.file instanceof File)
                );
                const hasSaveable = isSaveable(leftImg) || isSaveable(rightImg);

                // Check what's currently saved to avoid unnecessary localStorage overwrites (images omitted there)
                let skipLocalStorage = false;
                const currentSaved = localStorage.getItem(STORAGE_KEY);
                if (currentSaved) {
                    try {
                        const currentParsed = JSON.parse(currentSaved);
                        const currentFormData = JSON.stringify(currentParsed.formData || {});
                        const newFormData = JSON.stringify(dataToSave.formData);
                        if (currentFormData === newFormData &&
                            currentParsed.step === stepToSave &&
                            currentParsed.consent === consentToSave &&
                            currentParsed.mlConsentGiven === mlConsentGiven) {
                            skipLocalStorage = true;
                        }
                    } catch (e) {
                        // Continue with save if we can't parse current
                    }
                }

                if (!skipLocalStorage) {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
                }

                // Save image draft whenever we're on image step and formik holds saveable bytes (independent of localStorage dedup)
                if (stepToSave >= 1) {
                    if (hasSaveable) {
                        const toArrayBuffer = (obj) => {
                            if (!obj) return Promise.resolve(null);
                            if (obj instanceof File || obj instanceof Blob) {
                                return new Promise((resolve) => {
                                    const r = new FileReader();
                                    r.onload = () => resolve({ data: r.result, type: obj.type, name: obj.name || 'image' });
                                    r.onerror = () => resolve(null);
                                    r.readAsArrayBuffer(obj);
                                });
                            }
                            if (obj && typeof obj === 'object' && obj.data && obj.type) {
                                return Promise.resolve({ data: obj.data, type: obj.type, name: obj.name || 'image' });
                            }
                            // FilePond "proceeded" format: array with [0].file
                            if (Array.isArray(obj) && obj[0]?.file instanceof File) {
                                const f = obj[0].file;
                                return new Promise((resolve) => {
                                    const r = new FileReader();
                                    r.onload = () => resolve({ data: r.result, type: f.type, name: f.name || 'image' });
                                    r.onerror = () => resolve(null);
                                    r.readAsArrayBuffer(f);
                                });
                            }
                            return Promise.resolve(null);
                        };
                        (async () => {
                            try {
                                const [left_image, right_image] = await Promise.all([toArrayBuffer(leftImg), toArrayBuffer(rightImg)]);
                                if (!left_image && !right_image) return; // Don't overwrite draft with nulls
                                await saveScreeningImageDraft({
                                    patient_id: localStorage.getItem('patient_id'),
                                    report_id: localStorage.getItem('report_id'),
                                    left_image,
                                    right_image
                                });
                            } catch (e) {
                                /* best-effort */
                            }
                        })();
                    }
                }
            } catch (error) {
                /* persist best-effort */
            }
        }, 500); // Debounce by 500ms

        return () => clearTimeout(timeoutId);
    }, [formik.values, step, consent, mlConsentGiven, isEditMode]);

    // Clear saved data when user exits (form draft + image draft for reload recovery)
    const clearSavedData = async () => {
        suppressPersistAfterClearRef.current = true;
        localStorage.removeItem(STORAGE_KEY);
        try {
            await clearScreeningImageDraft();
        } catch (_) { /* best-effort */ }
    };

    // Reset to new patient form from inference screen (Add New Patient button)
    const handleAddNewPatient = async () => {
        initOpenCVWorker().catch((err) => {
            console.error('OpenCV worker init failed:', err);
        });

        localStorage.removeItem("patient_id");
        localStorage.removeItem("is_follow_up_patient");
        localStorage.removeItem("follow_up_other_id");
        localStorage.removeItem("report_id");

        // Await the IndexedDB image draft clear before resetting the form so the draft-restore
        // useEffect (which runs when step goes to 1) never reads back the old images.
        suppressPersistAfterClearRef.current = true;
        localStorage.removeItem(STORAGE_KEY);
        try {
            await clearScreeningImageDraft();
        } catch (_) {}
        suppressPersistAfterClearRef.current = false;

        const emptyFormValues = {
            pat_info: { ...pat_info },
            health_info: { ...health_info },
            image_file: {
                left: { ...image_file.left, image: null },
                right: { ...image_file.right, image: null },
                model_training_consent: normalizeModelTrainingConsent(image_file.model_training_consent),
                camera_used: image_file.camera_used || "",
                single_image_reason: image_file.single_image_reason || ""
            }
        };
        formik.resetForm({ values: emptyFormValues });
        setImageUploadSessionKey((k) => k + 1);
        setReportSubmitted(false);
        setInferenceState(prev => ({
            ...prev,
            inference: { left: "", right: "", req: "" },
            inferenceProvided: false
        }));
        setStep(0);
        setConsent(false);  /* Show consent form first, same flow as dashboard Add Patient */
        setMlConsentGiven(false);
    };

    const loadReportDataForEdit = async (reportId) => {
        try {
            // Load report data from IndexDB
            const report = await db.reports.where('id').equals(reportId).first();
            
            if (report) {
                // Load patient data
                const patient = await db.patients.where('id').equals(report.patient_id).first();
                
                if (patient) {
                    // Pre-fill form with existing data
                    const formData = {
                        pat_info: {
                            patientType: "new_patient",
                            name: patient.name || '',
                            age: patient.age || '',
                            gender: patient.sex || '',
                            diabetes: report.diabetes || '',
                            visualAcuity: {
                                distant: {
                                    left: report.visual_acuity_distant_left || '',
                                    right: report.visual_acuity_distant_right || '',
                                },
                                near: {
                                    left: report.visual_acuity_near_left || '',
                                    right: report.visual_acuity_near_right || '',
                                },
                            },
                            otherId: patient.other_id || '',
                            abhaId: patient.abha_id || '',
                            number: patient.phone || ''
                        },
                        health_info: {
                            preDiagnosedDiabetes: report.diabetes || '',
                            visionIssues: report.vision_issues || '',
                            singleImageReason: report.single_image_reason || '',
                            HbA1CLevel: '',
                            systolicBloodPressurebloodPressure: '',
                            diastolicBloodPressure: '',
                            visualAcuity: {
                                left: '',
                                right: ''
                            }
                        },
                        image_file: {
                            left: {
                                image: report.left_image || null,
                                gradabale: report.ml_model?.gradability?.left || '',
                                reason: report.single_image_reason || '',
                                classification: report.ml_model?.classification?.left || {},
                                gradability: report.ml_model?.gradability?.left || {}
                            },
                            right: {
                                image: report.right_image || null,
                                gradabale: report.ml_model?.gradability?.right || '',
                                reason: report.single_image_reason || '',
                                classification: report.ml_model?.classification?.right || {},
                                gradability: report.ml_model?.gradability?.right || {}
                            },
                            model_training_consent: normalizeModelTrainingConsent(report.model_training_consent),
                            single_image_reason: report.single_image_reason || ''
                        }
                    };

                    formik.setValues(formData);
                }
            }
        } catch (error) {
            /* edit load failed — form stays empty */
        }
    };

    function isEmptyObject(obj) {
        return Object.keys(obj).length === 0;
    }

    const handleModelResponse = (data, inference) => {
        
        const model_response = {
            classification: {},
            gradability: {},
            ishazy: {},
            reason: {},
            isReferable: {},
            nextSteps: {},
            model_outputs: {
                left: {},
                right: {}
            },
            operator_override: {
                left: {
                    fundus: false,
                    eye: false
                },
                right: {
                    fundus: false,
                    eye: false
                }
            }
        };
        for (const side of ['left', 'right']) {
            const classification = data[side]?.classification;
            const gradability = data[side]?.gradability;
            const ishazy = data[side]?.ishazy;
            const reason = data[side]?.reason;
            const operator_override_fundus = data[side]?.operator_override_fundus || false;
            const operator_override_eye = data[side]?.operator_override_eye || false;
            const model_outputs = data[side]?.model_outputs || {};
    
            // Check if classification exists and is not empty
            if (classification && !isEmptyObject(classification) && Object.keys(classification).length > 0) {
                model_response.classification[side] = classification;
            }
    
            if (gradability && !isEmptyObject(gradability) && Object.keys(gradability).length > 0) {
                model_response.gradability[side] = gradability;
            }
            model_response.ishazy[side] = ishazy || false;
            model_response.reason[side] = reason || '';
            
            // Store operator override flags
            model_response.operator_override[side].fundus = operator_override_fundus;
            model_response.operator_override[side].eye = operator_override_eye;
            
            // Store model outputs (fundus, eyes, DRDME)
            if (model_outputs.fundus) {
                model_response.model_outputs[side].fundus = model_outputs.fundus;
            }
            if (model_outputs.eyes) {
                model_response.model_outputs[side].eyes = model_outputs.eyes;
            }
            if (model_outputs.drdme) {
                model_response.model_outputs[side].drdme = model_outputs.drdme;
            }
            
            // Calculate isReferable for each side based on individual classification
            // For single image cases, this will be overridden by the overall referral logic
            if (classification && classification.predicted_class_name) {
                const operatorOverride = {
                    fundus: operator_override_fundus || false,
                    eye: operator_override_eye || false
                };
                const dmeGrade = model_outputs?.drdme?.DME_grade || null;
                model_response.isReferable[side] = RequiredRefferal(
                    classification.predicted_class_name,
                    null, // singleImageReason not needed for individual eye check
                    operatorOverride,
                    dmeGrade
                );
            } else {
                model_response.isReferable[side] = false;
            }
        }
        model_response.nextSteps = handleNextSteps(inference, data);
        
        return model_response;
    };

    const handleNextSteps = (data, imageFileData) => {
        // Helper function to extract DR grade from various formats
        const getDRGrade = (side) => {
            // 1. Check if inference state has direct DR string (and it's not empty)
            if (typeof data?.[side] === 'string' && data[side] && data[side].trim() !== '') {
                return data[side];
            }
            
            // 2. Check classification from imageFileData first (this is the source of truth)
            const classification = imageFileData?.[side]?.classification;
            const gradability = imageFileData?.[side]?.gradability;
            const modelOutputs = imageFileData?.[side]?.model_outputs;
            
            // If classification is already a DR grade string, use it directly
            if (typeof classification === 'string' && classification.trim() !== '') {
                return classification;
            }
            
            // If classification is an object, extract predicted_class_name and use handleSetDR
            if (classification && typeof classification === 'object' && classification !== null) {
                // Check if it's an object with predicted_class_name
                if (classification.predicted_class_name) {
                    const drGrade = handleSetDR(classification, gradability, modelOutputs);
                    if (drGrade) {
                        return drGrade;
                    }
                }
            }
            
            // 3. If classification is empty but model_outputs.drdme.DR_grade exists, use it
            if (!classification || (typeof classification === 'object' && Object.keys(classification).length === 0)) {
                if (modelOutputs?.drdme?.DR_grade) {
                    const drGrade = convertDRGradeToDisplay(modelOutputs.drdme.DR_grade);
                    if (drGrade) {
                        return drGrade;
                    }
                }
            }
            
            // 4. Try to get from nested structure in data
            const nestedClassification = data?.[side]?.classification;
            const nestedGradability = data?.[side]?.gradability;
            const nestedModelOutputs = data?.[side]?.model_outputs;
            
            if (nestedClassification) {
                // If it's already a string, use it
                if (typeof nestedClassification === 'string' && nestedClassification.trim() !== '') {
                    return nestedClassification;
                }
                // If it's an object, use handleSetDR
                if (typeof nestedClassification === 'object' && nestedClassification.predicted_class_name) {
                    const drGrade = handleSetDR(nestedClassification, nestedGradability, nestedModelOutputs);
                    if (drGrade) {
                        return drGrade;
                    }
                }
            }
            
            // 5. If nested classification is empty but nested model_outputs.drdme.DR_grade exists, use it
            if ((!nestedClassification || (typeof nestedClassification === 'object' && Object.keys(nestedClassification).length === 0)) && nestedModelOutputs?.drdme?.DR_grade) {
                const drGrade = convertDRGradeToDisplay(nestedModelOutputs.drdme.DR_grade);
                if (drGrade) {
                    return drGrade;
                }
            }
            
            return null;
        };
        
        const leftDR = getDRGrade('left');
        const rightDR = getDRGrade('right');
        
        // Get DME information for both eyes
        const getDMEGrade = (side) => {
            // Check imageFileData first
            const modelOutputs = imageFileData?.[side]?.model_outputs;
            if (modelOutputs?.drdme?.DME_grade) {
                return modelOutputs.drdme.DME_grade;
            }
            
            // Check nested structure in data
            const nestedModelOutputs = data?.[side]?.model_outputs;
            if (nestedModelOutputs?.drdme?.DME_grade) {
                return nestedModelOutputs.drdme.DME_grade;
            }
            
            return null;
        };
        
        const leftDME = getDMEGrade('left');
        const rightDME = getDMEGrade('right');
        
        // Get operator override information
        const getOperatorOverride = (side) => {
            // Check imageFileData first
            const operatorOverrideFundus = imageFileData?.[side]?.operator_override_fundus || false;
            const operatorOverrideEye = imageFileData?.[side]?.operator_override_eye || false;
            if (operatorOverrideFundus !== false || operatorOverrideEye !== false) {
                return { fundus: operatorOverrideFundus, eye: operatorOverrideEye };
            }
            
            // Check nested structure in data
            const nestedOperatorOverride = data?.[side]?.operator_override;
            if (nestedOperatorOverride) {
                return {
                    fundus: nestedOperatorOverride.fundus || false,
                    eye: nestedOperatorOverride.eye || false
                };
            }
            
            return { fundus: false, eye: false };
        };
        
        const operatorOverride = {
            left: getOperatorOverride('left'),
            right: getOperatorOverride('right')
        };
        
        const {message, date} = nextSteps(leftDR, rightDR, leftDME, rightDME, operatorOverride);
        const nextStep = `${message} on ${date}`;
        
        return nextStep;
    }

    const handleExit = () => {
        setOnExit(true);
    };

    const handleExitFormClose = () => {
        setOnExit(false);
    };

    const handleExitFormExit = () => {
        clearSavedData();
        navigate("/dashboard");
        localStorage.removeItem("patient_id");
        localStorage.removeItem("is_follow_up_patient"); // Clear follow-up flag
        localStorage.removeItem("follow_up_other_id"); // Clear follow-up otherId
        localStorage.removeItem("doc_id");
    };

    const handlePatientDetailsEditClick = () => {
        setInferenceState((prevState) => ({
            ...prevState,
            inference: {
              left: "",
              right: "",
              req: ""
            },
            inferenceProvided: false
        }));
        setConsent(true);
        setStep(0);
    };

    const handleHealthEditClick = () => {
        setInferenceState((prevState) => ({
            ...prevState,
            inference: {
              left: "",
              right: "",
              req: ""
            },
            inferenceProvided: false
        }));
        setConsent(true);
        setStep(1);
    };

    const handleImageEditClick = (eye=null) => {
        setInferenceState((prevState) => ({
            ...prevState,
            inference: {
              left: "",
              right: "",
              req: ""
            },
            inferenceProvided: false
        }));
        setConsent(true);
        setStep(1);
        if (eye) {
            const updatedImageFile = { ...formik.values.image_file };
            updatedImageFile[eye] = {
                gradabale: "",
                image: {},
                reason: "",
                classification: {},
                gradability: {},
                ishazy: false,
            };
            formik.setFieldValue("image_file", updatedImageFile);
        }
    }

    const handleSubmit = async (e) => {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }

        // Prevent a second submit while inference is already running (avoids doubling peak RAM on iOS)
        if (loading) return;

        // Models are already run during validation in imageUpload.js
        // Just use existing classification from formik - no need to run models again
        const leftClassification = formik.values.image_file?.left?.classification;
        const rightClassification = formik.values.image_file?.right?.classification;
        const singleImageMode = formik.values.image_file?.single_image_reason;

        // Check if classification exists
        // In single image mode, only one eye needs classification
        if (singleImageMode) {
            // Single image mode: at least one eye must have classification
            const hasLeft = leftClassification && Object.keys(leftClassification).length > 0;
            const hasRight = rightClassification && Object.keys(rightClassification).length > 0;
            if (!hasLeft && !hasRight) {
                setError('Classification not available. Please go back and upload images.');
                return;
            }
        } else {
            // Normal mode: both eyes must have classification
            if (!leftClassification || Object.keys(leftClassification).length === 0) {
                setError('Left eye classification not available. Please go back and upload images.');
                return;
            }
            if (!rightClassification || Object.keys(rightClassification).length === 0) {
                setError('Right eye classification not available. Please go back and upload images.');
                return;
            }
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const leftImage = formik.values.image_file?.left?.image;
            const rightImage = formik.values.image_file?.right?.image;

            // Classification was already computed during upload (imageUpload.js runs all three
            // models: fundus, DRDME, eyes). Use the stored values directly — no second inference
            // pass is needed or safe (sessions may be released on iOS; re-decoding a full-res
            // image would spike peak RAM on any platform). The classification check above already
            // guarantees these are non-empty when we reach this point.
            const leftResult = formik.values.image_file?.left?.classification || null;
            const rightResult = formik.values.image_file?.right?.classification || null;
            

            // Store in state for immediate access
            setClassificationResults(prev => ({ ...prev, left: leftResult, right: rightResult }));

            // Get operator override flags
            const operatorOverride = {
                left: {
                    fundus: formik.values.image_file?.left?.operator_override_fundus || false,
                    eye: formik.values.image_file?.left?.operator_override_eye || false
                },
                right: {
                    fundus: formik.values.image_file?.right?.operator_override_fundus || false,
                    eye: formik.values.image_file?.right?.operator_override_eye || false
                }
            };
            
            // Get DME grades from model outputs
            const dmeInfo = {
                left: formik.values.image_file?.left?.model_outputs?.drdme?.DME_grade || null,
                right: formik.values.image_file?.right?.model_outputs?.drdme?.DME_grade || null
            };

            // Use handleSetDR to get inference - supports both old (classification) and new (model_outputs) formats
            const leftInference = handleSetDR(
                leftResult,
                formik.values.image_file?.left?.gradability,
                formik.values.image_file?.left?.model_outputs
            );
            const rightInference = handleSetDR(
                rightResult,
                formik.values.image_file?.right?.gradability,
                formik.values.image_file?.right?.model_outputs
            );
            
            const referralAdvice = handleSetReferral(
                leftInference, 
                rightInference,
                formik.values.image_file?.single_image_reason,
                operatorOverride,
                dmeInfo
            );
            
            setInferenceState({
                inference: {
                    left: leftInference || null,
                    right: rightInference || null,
                    req: referralAdvice,
                },
                inferenceProvided: true,
            });

            // Advance to next step after successful inference
            setStep(step + 1);
            
            // Return the classification results directly so they can be used immediately
            return { left: leftResult, right: rightResult };

        } catch (err) {
            // Use the user-friendly error message if available
            const errorMessage = err.message || 'Failed to process images';
            setError(`Inference failed: ${errorMessage}`);
            
            captureAppError(err, {
                tags: {
                    errorType: 'model_inference_error',
                    errorStage: 'patient_screening',
                },
            });
            
            return { left: null, right: null };
        } finally {
            setLoading(false);
        }
    };

    const fundusCamera = localStorage.getItem('fundusCamera');

    // Refactored handleAddPatient: returns patientId (local or backend)
    const handleAddPatient = async () => {
        const otherId = formik.values.pat_info.otherId;
        
        // Check if offline using API health check
        const isReallyOffline = await checkIsOffline();
        
        if (isReallyOffline) {
            // Offline mode: Check IndexDB first
            const existingPatient = await db.patients.where('other_id').equals(otherId).first();
            
            if (existingPatient) {
                return { id: existingPatient.id, isTemp: existingPatient.syncStatus === 'pending', tempId: null };
            }
            
            // Create new patient in IndexDB
            const tempPatientId = generateSecureUUID();
            const userId = getCurrentUserId();
            const patientData = {
                id: tempPatientId,
                age: formik.values.pat_info.age,
                sex: formik.values.pat_info.gender,
                other_id: otherId,
                optometrist_id: userId,
                createdAt: new Date().toISOString(),
                syncStatus: 'pending'
            };
            await db.patients.put(patientData);
            return { id: tempPatientId, isTemp: true };
        } else {
            // Online mode: Check localStorage first, then API
            let patientId = localStorage.getItem("patient_id");
            
            if (patientId) {
                return { id: patientId, isTemp: false, tempId: null };
            }
            
            // Check if patient already exists in IndexDB with same other_id
            const existingPatient = await db.patients.where('other_id').equals(otherId).first();
            
            if (existingPatient) {
                // Return existing patient ID
                return { id: existingPatient.id, isTemp: existingPatient.syncStatus === 'pending', tempId: null };
            }

            // Create new patient if doesn't exist
            const tempPatientId = generateSecureUUID();
            const userId = getCurrentUserId();
            const patientData = {
                id: tempPatientId,
                age: formik.values.pat_info.age,
                sex: formik.values.pat_info.gender,
                other_id: otherId,
                optometrist_id: userId,
                createdAt: new Date().toISOString(),
                syncStatus: 'pending'
            };
            await db.patients.put(patientData);

            // Online: call API and update IndexDB
            try {
                const body = {
                    age: formik.values.pat_info.age,
                    sex: formik.values.pat_info.gender,
                    other_id: otherId
                };
                const response = await axios.post(
                    `${config.API_BASE_URL}/api/auth/patients/create/`,
                    body,
                    { headers: header }
                );
                
                if (response.data.id) {
                    // Update IndexDB with real patient ID
                    await db.patients.put({
                        ...patientData,
                        id: response.data.id,
                        syncStatus: 'synced'
                    });
                    // Delete temp patient from IndexDB
                    await db.patients.delete(tempPatientId);
                    return { id: response.data.id, isTemp: false, tempId: tempPatientId };
                }
            } catch (error) {
                // If API fails, keep the temp patient in IndexDB but mark it as pending
                // This ensures the patient data is preserved for later sync
                await db.patients.put({
                    ...patientData,
                    syncStatus: 'pending'
                });
                return { id: tempPatientId, isTemp: true }; // Return temp ID if API fails
            }
        }
    };

    // Always save to IndexDB first, then call API if online
    const handleSubmitReport = async (inferenceResults = null) => {
        // Set submitting state early to show ProcessingScreen
        setSubmittingReport(true);

        // Safety net: if submission hangs for >50s (API timeout / network stall), reload the
        // page rather than showing a frozen spinner forever. Data is already saved to IndexedDB
        // at this point so nothing is lost — it will sync on next session.
        const _submitTimeoutId = setTimeout(() => {
            window.location.reload();
        }, 50000);

        // Use the component-level edit mode variables
        const editReportId = urlParams.get('reportId');
        
        // Get classification from passed results first, then state, then formik values
        const leftClassification = inferenceResults?.left || classificationResults.left || formik.values.image_file?.left?.classification;
        const rightClassification = inferenceResults?.right || classificationResults.right || formik.values.image_file?.right?.classification;
        
        
        const imageFileData = {
            left: {
                classification: leftClassification || {},
                gradability: formik.values.image_file?.left?.gradability || {},
                ishazy: formik.values.image_file?.left?.ishazy || false,
                reason: formik.values.image_file?.left?.reason || '',
                operator_override_fundus: formik.values.image_file?.left?.operator_override_fundus || false,
                operator_override_eye: formik.values.image_file?.left?.operator_override_eye || false,
                model_outputs: formik.values.image_file?.left?.model_outputs || {
                    fundus: null,
                    eyes: null,
                    drdme: null
                }
            },
            right: {
                classification: rightClassification || {},
                gradability: formik.values.image_file?.right?.gradability || {},
                ishazy: formik.values.image_file?.right?.ishazy || false,
                reason: formik.values.image_file?.right?.reason || '',
                operator_override_fundus: formik.values.image_file?.right?.operator_override_fundus || false,
                operator_override_eye: formik.values.image_file?.right?.operator_override_eye || false,
                model_outputs: formik.values.image_file?.right?.model_outputs || {
                    fundus: null,
                    eyes: null,
                    drdme: null
                }
            }
        };
        
        const modelVersion = await getPersistedModelDownloadVersion();
        const ml_model = {
            ...handleModelResponse(imageFileData, inference),
            model_version:
                modelVersion != null && modelVersion !== '' ? String(modelVersion) : null,
        };
        const leftFile = formik.values.image_file.left.image;
        const rightFile = formik.values.image_file.right.image;
        const singleImageMode = formik.values.image_file.single_image_reason;
        const fundusCamera = localStorage.getItem("fundusCamera");

        // Only create File objects for images that exist
        let leftRenamed = null;
        let rightRenamed = null;
        // Blob/File for API and hashing (same as formik when File/Blob; Blob from data when restored from IndexedDB)
        let leftBlobForApi = null;
        let rightBlobForApi = null;

        const toStoredAndBlob = async (file, side) => {
            if (!file) return { stored: null, blob: null };
            // Already in IndexedDB format (e.g. restored from draft)
            if (typeof file === 'object' && file.data != null && file.type) {
                const stored = { data: file.data, type: file.type, name: file.name || (side === 'left' ? 'left.jpg' : 'right.jpg') };
                const blob = new Blob([file.data], { type: file.type });
                return { stored, blob };
            }
            if (file instanceof File || file instanceof Blob) {
                const arrayBuffer = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsArrayBuffer(file);
                });
                const stored = { data: arrayBuffer, type: file.type || 'image/jpeg', name: file.name || (side === 'left' ? 'left.jpg' : 'right.jpg') };
                return { stored, blob: file };
            }
            return { stored: null, blob: null };
        };

        try {
            const leftResult = await toStoredAndBlob(leftFile, 'left');
            leftRenamed = leftResult.stored;
            leftBlobForApi = leftResult.blob;
            const rightResult = await toStoredAndBlob(rightFile, 'right');
            rightRenamed = rightResult.stored;
            rightBlobForApi = rightResult.blob;
        } catch (err) {
            setSubmittingReport(false);
            setLoading(false);
            throw err;
        }
        

        // Handle patient data - either create new or update existing
        let patientResult;
        let patientId;
        let tempPatientId;
        
        if (isEditMode && editReportId) {
            // In edit mode, get the existing patient ID from the report
            const existingReport = await db.reports.where('id').equals(editReportId).first();
            
            if (existingReport) {
                patientId = existingReport.patient_id;
                tempPatientId = patientId;
                
                // Get current patient data and update it
                let currentPatient = await db.patients.where('id').equals(patientId).first();
                
                if (currentPatient) {
                    // Update existing patient data using put()
                    await db.patients.put({
                        ...currentPatient,
                        age: formik.values.pat_info.age,
                        sex: formik.values.pat_info.gender,
                        other_id: formik.values.pat_info.otherId
                    });
                } else {
                    // Patient doesn't exist - recreate it from formik values
                    const userId = getCurrentUserId();
                    const patientData = {
                        id: patientId,
                        age: formik.values.pat_info.age,
                        sex: formik.values.pat_info.gender,
                        other_id: formik.values.pat_info.otherId,
                        optometrist_id: userId,
                        createdAt: existingReport.createdAt || existingReport.created_at || new Date().toISOString(),
                        syncStatus: existingReport.syncStatus === 'synced' ? 'synced' : 'pending'
                    };
                    
                    // Save patient to IndexedDB
                    await db.patients.put(patientData);
                }
            } else {
                // Fallback to creating new patient if report not found
                patientResult = await handleAddPatient();
                patientId = patientResult.id;
                tempPatientId = patientResult.isTemp ? patientId : patientResult.tempId;
            }
        } else {
            // Normal mode - create new patient
            patientResult = await handleAddPatient();
            patientId = patientResult.id;
            tempPatientId = patientResult.isTemp ? patientId : patientResult.tempId;
        }

        // Generate image hashes for duplicate detection
        let leftImageHash = null;
        let rightImageHash = null;
        
        if (leftBlobForApi || (leftFile && (leftFile instanceof File || leftFile instanceof Blob))) {
            try {
                leftImageHash = await generateImageHash(leftBlobForApi || leftFile);
            } catch (error) {
                console.error('Error generating left image hash:', error);
            }
        }
        
        if (rightBlobForApi || (rightFile && (rightFile instanceof File || rightFile instanceof Blob))) {
            try {
                rightImageHash = await generateImageHash(rightBlobForApi || rightFile);
            } catch (error) {
                console.error('Error generating right image hash:', error);
            }
        }

        // Save image hashes after patient creation is complete (now we have the real patient ID)
        if (leftImageHash) {
            await saveImageHash(leftImageHash, patientId, 'left');
        }
        if (rightImageHash) {
            await saveImageHash(rightImageHash, patientId, 'right');
        }


        // Handle report data - either create new or update existing
        let reportId;
        
        if (isEditMode && editReportId) {
            // Update existing report - preserve existing images if not being updated
            reportId = editReportId;
            
            // Get existing report data to preserve images
            const existingReport = await db.reports.where('id').equals(editReportId).first();
            
            const updateData = {
                patient_id: patientId,
                optometrist_id: userData.id,
                ml_model,
                model_training_consent: normalizeModelTrainingConsent(
                    formik.values.image_file.model_training_consent
                ),
                diabetes: formik.values.pat_info.diabetes,
                diabetes_duration_years: formik.values.pat_info.diabetes === "Yes" ? (formik.values.pat_info.diabetesDurationYears || null) : null,
                diabetes_duration_months: formik.values.pat_info.diabetes === "Yes" ? (formik.values.pat_info.diabetesDurationMonths || null) : null,
                visual_acuity_distant_left: formik.values.pat_info.visualAcuity?.distant?.left || '',
                visual_acuity_distant_right: formik.values.pat_info.visualAcuity?.distant?.right || '',
                visual_acuity_near_left: formik.values.pat_info.visualAcuity?.near?.left || '',
                visual_acuity_near_right: formik.values.pat_info.visualAcuity?.near?.right || '',
                fundus_camera: fundusCamera,
                single_image_reason: singleImageMode,
                updatedAt: new Date().toISOString(),
                syncStatus: "pending"
            };
            
            // Handle image updates - always use current formik values
            
            // For left image - use ArrayBuffer format for IndexedDB storage
            if (leftRenamed) {
                updateData.left_image = leftRenamed;
                updateData.left_image_hash = leftImageHash;
            } else {
                updateData.left_image = null;
                updateData.left_image_hash = null;
            }
            
            // For right image - use ArrayBuffer format for IndexedDB storage
            if (rightRenamed) {
                updateData.right_image = rightRenamed;
                updateData.right_image_hash = rightImageHash;
            } else {
                updateData.right_image = null;
                updateData.right_image_hash = null;
            }
            
            // Check storage quota before saving images
            const imagesToSave = [leftRenamed, rightRenamed].filter(Boolean);
            if (imagesToSave.length > 0) {
                const { checkStorageForImages } = await import('../../utils/storageCheck');
                const storageCheck = await checkStorageForImages(imagesToSave);
                if (!storageCheck.hasSpace) {
                    throw new Error(`Storage full: ${storageCheck.message}`);
                }
            }
            
            // Use validation when updating report
            const { updateReportWithValidation } = await import('../../db');
            const updateResult = await updateReportWithValidation(editReportId, updateData);
            
            if (!updateResult.success) {
                throw new Error(`Failed to update report: ${updateResult.error}`);
            }
            
        } else {
            // Check storage quota before saving images
            const imagesToSave = [leftRenamed, rightRenamed].filter(Boolean);
            if (imagesToSave.length > 0) {
                const { checkStorageForImages } = await import('../../utils/storageCheck');
                const storageCheck = await checkStorageForImages(imagesToSave);
                if (!storageCheck.hasSpace) {
                    throw new Error(`Storage full: ${storageCheck.message}`);
                }
            }
            
            // Create new report using transaction to ensure patient exists
            reportId = generateSecureUUID();
            
            // Get current patient data to ensure it exists
            let currentPatient = await db.patients.get(patientId);
            
            // If patient doesn't exist, try to recreate it from formik values
            if (!currentPatient) {
                // Try to get patient from localStorage (might be online mode with real patient ID)
                const localStoragePatientId = localStorage.getItem("patient_id");
                if (localStoragePatientId && localStoragePatientId === patientId) {
                    // Patient ID from localStorage matches - try to get from API or recreate
                    const userId = getCurrentUserId();
                    const patientData = {
                        id: patientId,
                        age: formik.values.pat_info.age,
                        sex: formik.values.pat_info.gender,
                        other_id: formik.values.pat_info.otherId,
                        optometrist_id: userId,
                        createdAt: new Date().toISOString(),
                        syncStatus: 'synced' // Assume synced if from localStorage
                    };
                    
                    // Save patient to IndexedDB
                    await db.patients.put(patientData);
                    currentPatient = patientData;
                } else {
                    // Recreate patient from formik values
                    const userId = getCurrentUserId();
                    const patientData = {
                        id: patientId,
                        age: formik.values.pat_info.age,
                        sex: formik.values.pat_info.gender,
                        other_id: formik.values.pat_info.otherId,
                        optometrist_id: userId,
                        createdAt: new Date().toISOString(),
                        syncStatus: 'pending'
                    };
                    
                    // Save patient to IndexedDB
                    await db.patients.put(patientData);
                    currentPatient = patientData;
                }
            }
            
            // Final check - if still no patient, throw error
            if (!currentPatient) {
                throw new Error(`Cannot create report: patient with id ${patientId} does not exist and could not be recreated. Please go back and re-enter patient information.`);
            }
            
            // Use transaction to save patient and report atomically
            const { savePatientAndReportTransaction } = await import('../../db');
            
            const reportData = {
                id: reportId,
                patient_id: patientId,
                optometrist_id: userData.id,
                left_image: leftRenamed,
                right_image: rightRenamed,
                left_image_hash: leftImageHash,
                right_image_hash: rightImageHash,
                ml_model,
                model_training_consent: normalizeModelTrainingConsent(
                    formik.values.image_file.model_training_consent
                ),
                diabetes: formik.values.pat_info.diabetes,
                diabetes_duration_years: formik.values.pat_info.diabetes === "Yes" ? (formik.values.pat_info.diabetesDurationYears || null) : null,
                diabetes_duration_months: formik.values.pat_info.diabetes === "Yes" ? (formik.values.pat_info.diabetesDurationMonths || null) : null,
                visual_acuity_distant_left: formik.values.pat_info.visualAcuity?.distant?.left || '',
                visual_acuity_distant_right: formik.values.pat_info.visualAcuity?.distant?.right || '',
                visual_acuity_near_left: formik.values.pat_info.visualAcuity?.near?.left || '',
                visual_acuity_near_right: formik.values.pat_info.visualAcuity?.near?.right || '',
                fundus_camera: fundusCamera,
                single_image_reason: singleImageMode,
                createdAt: new Date().toISOString(),
                syncStatus: "pending"
            };
            
            const transactionResult = await savePatientAndReportTransaction(currentPatient, reportData);
            
            if (!transactionResult.success) {
                throw new Error(`Failed to save patient and report: ${transactionResult.error}`);
            }
        }

        // Always attempt the API call — do not gate on an offline probe here.
        // The probe can return false-positives on slow connections (3-second timeout), causing
        // valid online submissions to be silently saved to IndexedDB only ("sync page" problem).
        // If the API call throws (network error), the catch block handles it as an offline save.

        // Online: call report API.
        // Production (S3): submitReportWithPresign runs create -> presign -> PUT -> attach. UI marks
        // submitted only after the attach POST returns 2xx. On 400/404 from the presign endpoint
        // (local FileSystemStorage), we fall back to the legacy single multipart POST.
        const reportMetadata = {
            optometrist: userData.id,
            patient: patientId,
            ml_model,
            model_training_consent: normalizeModelTrainingConsent(
                formik.values.image_file.model_training_consent
            ),
            diabetes: formik.values.pat_info.diabetes,
            visual_acuity_distant_left: formik.values.pat_info.visualAcuity?.distant?.left || '',
            visual_acuity_distant_right: formik.values.pat_info.visualAcuity?.distant?.right || '',
            visual_acuity_near_left: formik.values.pat_info.visualAcuity?.near?.left || '',
            visual_acuity_near_right: formik.values.pat_info.visualAcuity?.near?.right || '',
            fundus_camera: fundusCamera,
        };
        applyDiabetesDurationForReportApi(
            reportMetadata,
            formik.values.pat_info.diabetes,
            formik.values.pat_info.diabetesDurationYears,
            formik.values.pat_info.diabetesDurationMonths,
        );
        if (singleImageMode && formik.values.image_file.single_image_reason) {
            reportMetadata.single_image_reason = formik.values.image_file.single_image_reason;
        }

        const leftName = (leftFile && (leftFile.name || leftFile.file?.name)) || (leftRenamed && leftRenamed.name) || null;
        const rightName = (rightFile && (rightFile.name || rightFile.file?.name)) || (rightRenamed && rightRenamed.name) || null;

        let offlineReportRowForResume = reportId ? await db.reports.get(reportId).catch(() => null) : null;
        const existingServerUuid = offlineReportRowForResume?.server_report_uuid || undefined;

        try {
            let response;
            if (isEditMode && editReportId) {
                // In edit mode, we don't submit to API since the report is already synced
                // Just show success message and keep PatientInference screen
                await clearSavedData(); // Clear saved form when report is updated
                // Keep image_file.* in formik so PatientInference thumbnails still render; cleared on Add New Patient.
                setSubmittingReport(false);
                setLoading(false); // Ensure loading is false so PatientInference shows
                setReportSubmitted(true);
                setAutoSubmitAfterInference(false); // Reset to show PatientInference screen
                // Ensure inferenceProvided stays true so PatientInference shows
                setInferenceState(prev => ({
                    ...prev,
                    inferenceProvided: true
                }));
                setSnackbarMessage("Report updated successfully!");
                setSnackbarSeverity("success"); // Use success for successful updates
                setShowOfflineSnackbar(true);
                return;
            } else {
                // Normal mode - submit new report to API.
                // Try presigned S3 flow first; fall back to multipart on FileSystemStorage backends.
                try {
                    response = await submitReportWithPresign({
                        apiBase: config.API_BASE_URL,
                        headers: header,
                        metadata: reportMetadata,
                        leftBlob: leftBlobForApi,
                        rightBlob: rightBlobForApi,
                        leftName,
                        rightName,
                        existingServerUuid,
                        onReportUuidCreated: async (srvUuid) => {
                            await db.reports.update(reportId, { server_report_uuid: srvUuid });
                            offlineReportRowForResume = {
                                ...(offlineReportRowForResume || {}),
                                server_report_uuid: srvUuid,
                            };
                        },
                    });
                } catch (presignErr) {
                    if (presignErr instanceof ResumeUploadBlockedError) {
                        throw presignErr;
                    }
                    if (presignErr instanceof PresignNotSupportedError) {
                        response = await multipartFallback({
                            apiBase: config.API_BASE_URL,
                            headers: header,
                            metadata: reportMetadata,
                            leftBlob: leftBlobForApi,
                            rightBlob: rightBlobForApi,
                            leftName,
                            rightName,
                        });
                    } else {
                        throw presignErr;
                    }
                }
            }
            
            // Check if the API call was successful (201 Created is also success)
            if (response.status === 200 || response.status === 201) {
                // Save image hashes to backend separately
                // Get the unsynced hashes from IndexDB and format for backend
                const currentUserId = getCurrentUserId();
                
                const unsyncedHashes = await db.image_hashes
                    .where('optometrist_id').equals(currentUserId)
                    .and(hash => hash.synced === false)
                    .toArray();
                
                if (unsyncedHashes.length > 0) {
                    try {
                        // Format hashes for backend (same format as add report API)
                        const hashesForBackend = unsyncedHashes.map(hash => {
                            return {
                                hash: hash.hash,
                                optometrist_id: hash.optometrist_id, // Use same format as stored
                                patient_id: hash.patient_id, // Use same format as stored
                                eye: hash.eye
                            };
                        });
                        
                        const hashResponse = await axios.post(
                            `${config.API_BASE_URL}/api/reports/image-hashes/sync/`,
                            { hashes: hashesForBackend },
                            { headers: header }
                        );
                        
                        // Check if the API call was successful
                        if (hashResponse.status === 200 || hashResponse.status === 201) {
                            // Mark image hashes as synced
                            await markImageHashesAsSynced(unsyncedHashes);
                        } else {
                        }
                    } catch (hashError) {
                        captureAppError(hashError, {
                            tags: { stage: 'submit_report', errorType: 'image_hashes' },
                            level: 'warning',
                        });
                    }
                }
                
                // Delete patient with cascade deletion (deletes associated reports automatically)
                const { deletePatientWithCascade } = await import('../../db');
                const deleteResult = await deletePatientWithCascade(patientId);
                
                if (!deleteResult.success) {
                    // Fallback: manually delete report if cascade failed
                    await db.reports.delete(reportId);
                }
                
                localStorage.setItem("report_id", response.data.uuid);
                await clearSavedData(); // Draft + IDB image draft only — not formik blobs (PatientInference needs them for preview)
                setSubmittingReport(false);
                setLoading(false); // Ensure loading is false so PatientInference shows
                setReportSubmitted(true);
                setAutoSubmitAfterInference(false); // Reset to show PatientInference screen
                // Ensure inferenceProvided stays true so PatientInference shows
                setInferenceState(prev => ({
                    ...prev,
                    inferenceProvided: true
                }));
                // Stay on PatientInference screen (Screening Report) - don't navigate away
                // The PatientInference component will show with Home, Export, and Add Patient buttons
                setSnackbarMessage("Report submitted successfully!");
                setSnackbarSeverity("success");
                setShowOfflineSnackbar(true);
                return; // Exit early on success
            } else {
                setSubmittingReport(false);
                setLoading(false);
                captureAppError(
                    new Error(`Report submit unexpected status: ${response?.status}`),
                    {
                        tags: { stage: 'submit_report', errorType: 'unexpected_status' },
                        extra: { status: response?.status, patientId, reportId },
                    }
                );
                alert("Error Submitting Report!!");
            }
        } catch (error) {
            // Reset UI state
            setSubmittingReport(false);
            setReportSubmitted(false); // Don't mark as submitted if storage error
            
            if (error instanceof ResumeUploadBlockedError) {
                captureAppError(error, {
                    tags: { stage: 'submit_report', errorType: 'resume_upload_blocked' },
                    level: 'warning',
                });
                setSubmittingReport(false);
                setLoading(false);
                setReportSubmitted(true);
                setAutoSubmitAfterInference(false);
                setInferenceState(prev => ({
                    ...prev,
                    inferenceProvided: true
                }));
                setSnackbarMessage(error.message);
                setSnackbarSeverity("warning");
                setShowOfflineSnackbar(true);
                return;
            }
            
            // Check if it's a storage quota error
            if (error.message?.includes('Storage full') || error.name === 'QuotaExceededError') {
                setSubmittingReport(false);
                setLoading(false);
                setSnackbarMessage(error.message || "Storage is full. Please sync your data or clear browser storage.");
                setSnackbarSeverity("error"); // Use error severity for storage issues
                setShowOfflineSnackbar(true);
                
                captureAppError(error, {
                    tags: {
                        errorType: 'storage_quota_exceeded',
                        stage: 'save_report',
                    },
                });
                return;
            }
            
            captureAppError(error, {
                tags: {
                    stage: 'submit_report',
                    errorType: error.response ? 'api_error' : 'network_or_client',
                },
                extra: { patientId, reportId },
            });

            // Report API failed - data is saved locally; stay on Patient Inference (same as online)
            setSubmittingReport(false);
            setLoading(false);
            setReportSubmitted(true);
            setAutoSubmitAfterInference(false);
            setInferenceState(prev => ({
                ...prev,
                inferenceProvided: true
            }));
            setSnackbarMessage("Connection lost. Your data has been saved and will sync when online.");
            setSnackbarSeverity("info"); // Use info for offline messages
            setShowOfflineSnackbar(true);
            // Report row stays in IndexedDB for sync; drop reload draft (same as online submit).
            await clearSavedData();
            // Don't delete pending report from IndexedDB — sync needs it later
        } finally {
            clearTimeout(_submitTimeoutId);
            // Always clear loader so it never stays stuck (e.g. if success path threw after snackbar)
            setSubmittingReport(false);
            setLoading(false);
        }
    };

    const {inference, inferenceProvided} = inferenceState;

    return (
        <>
            <SyncValidationGuard>
                {checkingRedirect && location.pathname === '/patientscreen' && !isEditMode ? (
                    <FullPageLoader />
                ) : (
                    inferenceProvided && !submittingReport && !loading && !autoSubmitAfterInference ?
                        <PatientInference
                            formik={formik}
                            inference={inference}
                            handleExit={handleExit}
                            handlePatientDetailsEditClick={handlePatientDetailsEditClick}
                            handleImageEditClick={handleImageEditClick}
                            handleHealthEditClick={handleHealthEditClick}
                            handleSubmitReport={handleSubmitReport}
                            submittingReport={submittingReport}
                            reportSubmitted={reportSubmitted}
                            setStep={setStep}
                            setInferenceState={setInferenceState}
                            setConsent={setConsent}
                            onAddNewPatient={handleAddNewPatient}
                        />
                        :
                        (loading || submittingReport || autoSubmitAfterInference ? (
                            <ProcessingScreen
                              title={
                                submittingReport || autoSubmitAfterInference
                                  ? "Submitting your report"
                                  : "AI Inference is being Generated"
                              }
                              subtitle={
                                submittingReport || autoSubmitAfterInference
                                  ? "Please wait while we save your screening report. This may take a minute."
                                  : "Sit tight, as we process your request, this might take a few minutes"
                              }
                            />
                        ) :
                            <PatientScreening
                            formik={formik}
                            handleSubmit={handleSubmit}
                            handleSubmitReport={handleSubmitReport}
                            setAutoSubmitAfterInference={setAutoSubmitAfterInference}
                            handleExitFormExit={handleExitFormExit}
                            handleExit={handleExit}
                            consent={consent}
                            setConsent={setConsent}
                            step={step}
                            setStep={setStep}
                            mlConsentGiven={mlConsentGiven}
                            setMlConsentGiven={setMlConsentGiven}
                            imageUploadSessionKey={imageUploadSessionKey}
                        />
                        )
                    )
                }
            </SyncValidationGuard>
            <div>
                {onExit && <ExitForm open={onExit} handleExitFormClose={handleExitFormClose} handleExitFormExit={handleExitFormExit} />}
            </div>
            
            {/* Offline Snackbar */}
            <Snackbar
                open={showOfflineSnackbar}
                autoHideDuration={snackbarSeverity === 'error' ? 8000 : 4000} // Show error messages longer
                onClose={() => {
                    setShowOfflineSnackbar(false);
                    setSnackbarSeverity("info"); // Reset to default
                }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => {
                        setShowOfflineSnackbar(false);
                        setSnackbarSeverity("info"); // Reset to default
                    }} 
                    severity={snackbarSeverity}
                    sx={{ 
                        width: '100%',
                        maxWidth: '90vw',
                        ...(snackbarSeverity === 'error' && {
                            backgroundColor: '#fee',
                            color: '#c33',
                            border: '1px solid #fcc'
                        })
                    }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default PatientScreeningWrapper;